import { useEffect, useMemo, useState } from "react";
import { Breadcrumb, Empty, Pagination, Tabs } from "antd";
import { useHistory } from "react-router-dom";
import projectDefault from "../../img/project_default.jpg";
import { fetchMySaleActivityList } from "./api";
import { getSaleActivityAuditState } from "./status";
import { SaleActivityInfo } from "./types";
import "./activity.scss";

function statusClass(text?: string) {
  if (!text) return "";
  if (text.includes("通过") || text.includes("认证")) return "is-pass";
  if (text.includes("拒") || text.includes("驳")) return "is-reject";
  if (text.includes("结束")) return "is-end";
  if (text.includes("中")) return "is-active";
  if (text.includes("待") || text.includes("未")) return "is-pending";
  return "is-pending";
}

function ActivityCard({
  item,
  type,
  mode,
  onClick,
}: {
  item: SaleActivityInfo;
  type: "expert" | "user";
  mode: "joined" | "audit";
  onClick: () => void;
}) {
  const auditState = getSaleActivityAuditState(item, type);
  const auditStatusText = auditState.text;

  return (
    <div className={`sale-activity-my-grid-card sale-activity-my-grid-card--${mode}`} onClick={onClick}>
      <div className="sale-activity-my-grid-card__cover">
        <img src={item.pictureUrl || projectDefault} alt={item.name} />
      </div>
      <div className="sale-activity-my-grid-card__body">
        <h4 title={item.name}>{item.name || "活动名称"}</h4>
        <div className="sale-activity-my-grid-card__status">
          <span>
            <em>活动状态：</em>
            <strong className={statusClass(item.activityLearnStatusText)}>{item.activityLearnStatusText || "--"}</strong>
          </span>
          <span>
            <em>审批状态：</em>
            <strong className={statusClass(auditStatusText)}>{auditStatusText || "--"}</strong>
          </span>
        </div>
      </div>
    </div>
  );
}

function ActivityList({ type }: { type: "expert" | "user" }) {
  const history = useHistory();
  const [list, setList] = useState<SaleActivityInfo[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 8;

  const load = (currentPage = page) => {
    fetchMySaleActivityList(type, {
      activityInfo: {},
      page: { currentPage, pageSize },
    }).then((res) => {
      const data = res.data || {};
      setList(data.activityList || data.list || []);
      setTotal(data.page?.totalResultSize || 0);
      setPage(data.page?.currentPage || currentPage);
    });
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const groups = useMemo(() => {
    const joined: SaleActivityInfo[] = [];
    const auditing: SaleActivityInfo[] = [];
    list.forEach((item) => {
      const auditState = getSaleActivityAuditState(item, type);
      if (auditState.passed) {
        joined.push(item);
      } else {
        auditing.push(item);
      }
    });
    return { joined, auditing };
  }, [list, type]);

  const jump = (item: SaleActivityInfo) => {
    const auditState = getSaleActivityAuditState(item, type);
    if (auditState.passed) {
      history.push(`/saleActivityLearnDetail?activityId=${item.activityId}&type=${type}`);
      return;
    }
    history.push(`/saleActivityAuditDetail?activityId=${item.activityId}&type=${type}`);
  };

  const renderGroup = (title: string, data: SaleActivityInfo[], mode: "joined" | "audit") => (
    <section className="sale-activity-my-group">
      <div className="sale-activity-my-group__head">
        <h3>{title}</h3>
      </div>
      {data.length > 0 ? (
        <div className="sale-activity-my-grid">
          {data.map((item) => (
            <ActivityCard key={item.activityId} item={item} type={type} mode={mode} onClick={() => jump(item)} />
          ))}
        </div>
      ) : (
        <div className="sale-activity-my-empty"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`暂无${title}`} /></div>
      )}
    </section>
  );

  const auditTitle = type === "expert" ? "专家报名审批" : "学员报名审批";

  return (
    <>
      {renderGroup("已参加", groups.joined, "joined")}
      {renderGroup(auditTitle, groups.auditing, "audit")}
      {total > pageSize && (
        <div className="sale-activity-my-pagination">
          <Pagination total={total} current={page} pageSize={pageSize} onChange={(p) => load(p)} />
        </div>
      )}
    </>
  );
}

export default function MySaleActivity() {
  const [activeKey, setActiveKey] = useState<"expert" | "user">("expert");

  return (
    <div className="sale-activity-my-page">
      <div className="sale-activity-my-breadcrumb">
        <Breadcrumb separator=">">
          <Breadcrumb.Item>我的活动</Breadcrumb.Item>
          <Breadcrumb.Item>{activeKey === "expert" ? "专家活动" : "学员活动"}</Breadcrumb.Item>
        </Breadcrumb>
      </div>
      <Tabs activeKey={activeKey} onChange={(key) => setActiveKey(key as "expert" | "user")}>
        <Tabs.TabPane key="expert" tab="专家活动"><ActivityList type="expert" /></Tabs.TabPane>
        <Tabs.TabPane key="user" tab="学员活动"><ActivityList type="user" /></Tabs.TabPane>
      </Tabs>
    </div>
  );
}
